"use client";
import EventCalenderIcon from "@/assets/images/icon/event-calender.svg";
import TrashIcon from "@/assets/images/icon/trash.svg";
import Loader from "@/components/Loader/Loader";
import ReUsableSelect from "@/components/summer-camp/UsableSelect";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import {
  setSearchData,
  setSummerCampEvents,
  setSummerCampEventsCount,
} from "@/redux/features/summerCampSlice";
import axiosInstance from "@/service/axiosInstance";
import {
  APP_NAME,
  ClassificationTypes,
  CommonConstant,
  GenderOptions,
  initialFormData,
  MultipleMonthsSummerCampSchools,
  ORDER_TYPE,
  PRIVATE_PRODUCTS_ACCESS_ROLES,
  StudentTypes,
  SummerCampTypes,
  SummerCampTypesTexts,
  ToastOptions,
  CampType,
} from "@/utils/enum";
import { FormData, ReactSelectOptions } from "@/utils/interface";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
dayjs.extend(customParseFormat);

import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  extractDownloadLinks,
  getCategorizedLinks,
} from "@/utils/extractDownloadLinks";
import DownloadLinkButton from "@/components/common/DownloadLinkButton";
import { getVenueTypeOptions } from "@/utils/utils";
const Page = () => {
  const [loading, setLoading] = useState(false);
  const [formOptions, setFormOptions] = useState<any>({
    schools: [],
    grades: [],
  });
  const [formData, setFormData] = useState<FormData>({ ...initialFormData });
  const [formLoading, setFormLoading] = useState(false);
  const [isMaxcapacity, setIsMaxcapacity] = useState(false);
  const [showPrivate, setShowPrivate] = useState(false);
  const [showError, setShowError] = useState<Record<string, boolean>>({
    campType: false,
    venueSchool: false,
    campMonth: false,
    grade: false,
    gender: false,
    enrollmentNumber: false,
    studentType: false,
    venueType: false,
  });
  const [isRestoringData, setIsRestoringData] = useState(true);
  const [isExternalApp, SetIsExternalApp] = useState(false);
  const campMonthOptions: any = useRef([]);
  const gradesMapping: any = useRef({});
  const studentInfo: any = useRef({});
  const eventsDuration: any = useRef({});
  const [venueTypeOptions, setVenueTypeOptions] = useState<any[]>([
    { label: "All", value: "All" },
  ]);
  const [eventsVersion, setEventsVersion] = useState(0);
  const cachedSchoolEvents: any = useRef([]);

  const dispatch = useDispatch();
  const router = useRouter();
  const userRole = useSelector((state: any) => state.auth.userRole);
  const [categorizedLinks, setCategorizedLinks] = useState<any>(null);
  const [isLoadingSchoolData, setIsLoadingSchoolData] = useState(false);
  const app_name = process.env.NEXT_PUBLIC_APP_NAME || "";
  const getSchools = async () => {
    try {
      const data = await axiosInstance.get("/locations");
      if (!data.data) {
        toast.error("Unable to fetch schools. Please try again.", ToastOptions);
      } else {
        let schoolData: any = [];
        data.data.map((doc: any) => {
          schoolData.push({
            value: doc?.attributes?.School_Name?.data?.attributes?.name,
            label: doc?.attributes?.School_Name?.data?.attributes?.name,
          });
        });

        const sortedSchoolData = schoolData.sort((a: any, b: any) => {
          const schoolA = a.value.toUpperCase();
          const schoolB = b.value.toUpperCase();

          if (schoolA < schoolB) {
            return -1;
          }
          if (schoolA > schoolB) {
            return 1;
          }
          return 0;
        });

        return sortedSchoolData;
      }
    } catch (error: any) {
      console.log("Error in getting locations :>> ", error.data || error);
      return null;
    }
  };
  const getMetabaseSchools = async () => {
    try {
      const response = await axiosInstance.post(`/metabase-schools`);
      const data = response;

      if (!data || !Array.isArray(data)) {
        toast.error("Failed to fetch schools", ToastOptions);
        return null;
      }

      let schoolData = data.map((item: any) => ({
        value: item.venue_name,
        label: item.venue_name,
      }));

      const sortedSchoolData = schoolData.sort((a, b) => {
        const schoolA = a.value.toUpperCase();
        const schoolB = b.value.toUpperCase();
        return schoolA.localeCompare(schoolB);
      });

      return sortedSchoolData;
    } catch (error: any) {
      console.log(
        "Error in getSchools :>> ",
        error.response?.data || error.message,
      );
      return null;
    }
  };

  const getGrades = async () => {
    try {
      const { data } = await axiosInstance.get("/grade");

      let structuredGradeData: any = [];

      for (let i = 0; i < data.length; i++) {
        structuredGradeData.push({
          label: data[i]?.attributes?.name,
          value: data[i]?.attributes?.Calculation_Value,
          order: data[i]?.attributes?.order,
        });
      }

      structuredGradeData.sort((a: any, b: any) => a.order - b.order);
      const newGradesMapping = structuredGradeData.reduce(
        (acc: any, obj: any) => {
          acc[obj.label] = obj;
          return acc;
        },
        {},
      );

      newGradesMapping["Sr. K.G."] = newGradesMapping["Sr.KG"];
      newGradesMapping["Jr. K.G."] = newGradesMapping["Jr.KG"];

      gradesMapping.current = { ...newGradesMapping };

      return structuredGradeData;
    } catch (error: any) {
      console.log("Error in loading grades :>> ", error.data || error);
    }
  };

  const getFormOptions = async () => {
    try {
      let schools;
      setLoading(true);
      if (app_name == APP_NAME.SUMMER_CAMP_PRIVATE) {
        schools = await getMetabaseSchools();
      } else {
        schools = await getSchools();
      }
      if (!schools) throw new Error("Unable to find schools");

      const grades = await getGrades();
      if (!grades) throw new Error("Unable to find grades");

      if (schools && grades) {
        setFormOptions({ schools, grades });
      } else {
        throw new Error("Unable to find schools & grades");
      }
    } catch (error) {
      toast.error(
        "Unable to process data at the moment. Please try again later.",
        ToastOptions,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCampType = (campType: string) => {
    try {
      if (formData.campType == campType && formData.venueSchool != null) {
        toast.error(
          "Please select a Camp Type to proceed further.",
          ToastOptions,
        );
      }
      if (formData.campType == campType) {
        const updatedformData = { ...formData };
        updatedformData.campType = "";
        campMonthOptions.current = [];
        setFormData({
          ...updatedformData,
          campMonth: null,
          venueType: null,
        });
      } else {
        if (formData.venueSchool) {
          updateMonths(campType);
        }
        setShowError((prev) => ({ ...prev, campType: false }));
        setFormData({
          ...formData,
          campType,
          campMonth: null,
          venueType: null,
        });
      }
    } catch (error) {
      alert(error);
    }
  };

  const handlePrivateProducts = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = e.target.checked;
    setShowPrivate(isChecked);

    if (formData.venueSchool) {
      await fetchAndProcessSchoolData(formData.venueSchool, isChecked);
    }
  };

  const handleSchoolChange = async (e: any, privateEvents = showPrivate) => {
    // Update form data
    setShowError((prev) => ({ ...prev, venueSchool: false }));
    setFormData({ ...formData, venueSchool: e, campMonth: null });

    // Show camp type selection message if needed
    if (e && !formData.campType) {
      toast.error("Please select a Camp Type to proceed further", ToastOptions);
    }

    // Fetch and process school data (single API call)
    await fetchAndProcessSchoolData(e, privateEvents);
  };
  const handleCampMonthChange = (e: any) => {
    setShowError((prev) => ({ ...prev, campMonth: false }));
    setFormData((prev) => ({ ...prev, campMonth: e, venueType: null }));
  };

  const handleGradeChange = (e: any) => {
    setShowError((prev) => ({ ...prev, grade: false }));
    setFormData((prev) => ({ ...prev, grade: e }));
  };

  const handleGenderChange = (e: any) => {
    setShowError((prev) => ({ ...prev, gender: false }));

    setFormData((prev) => ({ ...prev, gender: e }));
  };
  const handleVenueTypeChange = (e: any) => {
    setShowError((prev) => ({ ...prev, venueType: false }));
    setFormData({ ...formData, venueType: e });
  };
  const validateForm = () => {
    try {
      const newErrors: Record<string, boolean> = {
        campType: !SummerCampTypes.includes(formData.campType),
        venueSchool: !formData.venueSchool,
        campMonth: !formData.campMonth,
        grade: !formData.grade,
        gender: !formData.gender,
        studentType: !formData.studentType,
        venueType: !formData.venueType,
        enrollmentNumber:
          formData.studentType?.value !== CommonConstant.non_vibgyor_student &&
          !formData.enrollmentNumber,
      };

      setShowError(newErrors);
      let isValidated = true;

      switch (true) {
        case !SummerCampTypes.includes(formData.campType):
          isValidated = false;
          break;

        case !formData.venueSchool:
          isValidated = false;
          break;

        case !formData.campMonth:
          isValidated = false;
          break;

        case !formData.grade:
          isValidated = false;
          break;

        case !formData.gender:
          isValidated = false;
          break;

        case !formData.enrollmentNumber:
          if (
            formData.studentType?.value == CommonConstant.non_vibgyor_student
          ) {
            isValidated = true;
          } else {
            isValidated = false;
          }
          break;

        case !formData.studentType:
          isValidated = false;
          break;

        case !formData.venueType:
          isValidated = false;
          break;
      }

      return isValidated;
    } catch (error) {
      return false;
    }
  };

  const searchEvent = async () => {
    try {
      setFormLoading(true);
      setLoading(true);
      if (validateForm()) {
        const requestData = {
          ...formData,
          privateEvents: showPrivate,
        };
        const { data, meta }: any = await axiosInstance.post(
          "/events/summer-camp",
          requestData,
        );

        if (!data || data.length == 0) {
          toast.info(
            "There are no events available at the moment.",
            ToastOptions,
          );
          setLoading(false);
          return;
        }
        if (
          data[0].attributes.maxcapacity_reached &&
          formData.campType == CampType.Full_Day
        ) {
          toast.info(
            "There are no events available at the moment.",
            ToastOptions,
          );
          setIsMaxcapacity(true);
          setLoading(false);
          return;
        } else {
          setIsMaxcapacity(false);
        }

        if (data.length == 0) {
          toast.info(
            "No events were found based on your filters.",
            ToastOptions,
          );
          setLoading(false);
          return;
        }

        dispatch(setSummerCampEvents(data));
        dispatch(setSummerCampEventsCount(meta.pagination.total));
        dispatch(setSearchData(formData));

        const newRegFormData = {
          firstName: studentInfo.current.firstName || "",
          lastName: studentInfo.current.lastName || "",
          phoneNumber: studentInfo.current.phoneNumber || "",
          email: studentInfo.current.email || "",
          schoolName: studentInfo.current.schoolName || null,
          otherSchoolName: "",
          selectedEvents: [],
          url: "",
          ...formData,
        };

        localStorage.setItem(
          "summerCampFormData",
          JSON.stringify(newRegFormData),
        );

        router.push("summer-camp/register");
      } else {
        toast.error(
          "Required fields are missing. Please verify your input.",
          ToastOptions,
        );
        setLoading(false);
      }
    } catch (error: any) {
      console.error(error.message || error.data || error);
      setLoading(false);
    } finally {
      setLoading(false);
      setFormLoading(false);
    }
  };

  const handleEnrollmentNumberChange = (e: any) => {
    setShowError((prev) => ({ ...prev, enrollmentNumber: false }));
    setFormData({
      ...formData,
      enrollmentNumber: e.target.value.toUpperCase().trim(),
    });
  };

  const onENRNoBlur = async (e: any) => {
    try {
      if (formData.studentType?.value == CommonConstant.non_vibgyor_student)
        return;
      setFormLoading(true);
      if (e.target.value.length == 0) {
        return;
      }

      const ernNo = e.target.value;
      let prevValues = structuredClone(formData);

      if (ernNo) {
        const public_token = process.env.NEXT_PUBLIC_API_TOKEN;
        const { data } = await axiosInstance.get(`/users/${ernNo}`, {
          headers: {
            "x-api-token": `Bearer ${public_token}`,
          },
        });

        if (data) {
          let {
            Firstname,
            Lastname,
            MobilePreferred,
            phone_number,
            EmailPreferred,
            Gender,
            Class,
            School,
          } = data;

          if (Gender == Gender.F) {
            Gender = ClassificationTypes.Female;
          } else {
            Gender = ClassificationTypes.Male;
          }
          School = School.toUpperCase();

          const updatedFormData = {
            ...prevValues,
            grade: gradesMapping.current[Class],
            gender: { label: Gender, value: Gender },
          };

          const studentData = {
            firstName: Firstname,
            lastName: Lastname,
            phoneNumber: MobilePreferred || phone_number,
            email: EmailPreferred,
            schoolName: { label: School, value: School },
            otherSchoolName: "",
          };

          studentInfo.current = { ...studentData };

          setFormData({ ...updatedFormData });
        }
      }
    } catch (error: any) {
      console.log(
        JSON.stringify({
          message: "Error in fetching ENR. number from database",
          errorMessage: error.message ?? error,
        }),
      );
    } finally {
      setFormLoading(false);
    }
  };

  const handleStudentType = (e: any) => {
    setShowError((prev) => ({ ...prev, studentType: false }));
    setFormData({ ...formData, studentType: e });
  };

  const getEventsInSchool = async (
    school: ReactSelectOptions,
    privateEvents = false,
  ) => {
    try {
      const { data }: any = await axiosInstance.post(
        "/events/summer-camp/get-school-events",
        { venueSchool: school, privateEvents },
      );

      if (!data) {
        toast.error(
          "We were unable to find any events for selected school.",
          ToastOptions,
        );
        return;
      }

      return data;
    } catch (error: any) {
      console.error(error.message || error.data || error);
    }
  };

  const updateMonths = (campType: string) => {
    campMonthOptions.current = eventsDuration.current[campType];
    if (!campMonthOptions.current || campMonthOptions.current.length == 0) {
      toast.info(
        "Camp duration not available for the selected Camp Type and Venue School.",
        ToastOptions,
      );
    }
  };
  const fetchAndProcessSchoolData = async (
    school: any,
    privateEvents = showPrivate,
    campType?: string,
  ) => {
    if (!school) {
      setCategorizedLinks(null);
      campMonthOptions.current = [];
      setVenueTypeOptions([]);
      cachedSchoolEvents.current = [];
      return;
    }

    setIsLoadingSchoolData(true);

    try {
      const events = await getEventsInSchool(school, privateEvents);

      if (!events || events.length === 0) {
        setCategorizedLinks(null);
        campMonthOptions.current = [];
        setVenueTypeOptions([]);
        cachedSchoolEvents.current = [];
        setEventsVersion((v) => v + 1);
        return;
      }
      cachedSchoolEvents.current = events;
      setEventsVersion((v) => v + 1);

      const rulesRegulation = events[0]?.attributes?.rules_regulation || "";
      const links = extractDownloadLinks(rulesRegulation);
      const categorized = getCategorizedLinks(links);
      setCategorizedLinks(categorized);

      const categorisedData = events.reduce((acc: any, current: any) => {
        const logicName = current.attributes.logic_name;
        const eventStartDate =
          current.attributes.fees_id.data.attributes.activity_start_date;
        const eventEndDate =
          current.attributes.fees_id.data.attributes.activity_end_date;

        if (!acc[logicName]) {
          acc[logicName] = [];
        }

        const isDuplicate = acc[logicName].some(
          (event: any) =>
            event.startDate === eventStartDate &&
            event.endDate === eventEndDate,
        );

        if (!isDuplicate) {
          acc[logicName].push({
            label: `${dayjs(eventStartDate).format("D MMMM")} to ${dayjs(
              eventEndDate,
            ).format("D MMMM")}`,
            value: `${eventStartDate}_${eventEndDate}`,
            startDate: eventStartDate,
            endDate: eventEndDate,
          });
        }

        return acc;
      }, {});

      eventsDuration.current = { ...categorisedData };

      const campTypeToUse = campType || formData.campType;
      if (campTypeToUse) {
        updateMonths(campTypeToUse);
      }
    } catch (error) {
      console.error("Error fetching school data:", error);
      setCategorizedLinks(null);
      campMonthOptions.current = [];
      setVenueTypeOptions([]);
      cachedSchoolEvents.current = [];
      toast.error(
        "Unable to load school information. Please try again.",
        ToastOptions,
      );
    } finally {
      setIsLoadingSchoolData(false);
    }
  };
  useEffect(() => {
    const initialize = async () => {
      await getFormOptions();
      if (app_name === APP_NAME.SUMMER_CAMP_PRIVATE) {
        SetIsExternalApp(true);

        const nonVibgyorOption = StudentTypes.find(
          (type) => type.value === CommonConstant.non_vibgyor_student,
        );
        if (nonVibgyorOption) {
          setFormData((prev) => ({
            ...prev,
            studentType: nonVibgyorOption,
          }));
        }
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    const restoreSavedFormData = async () => {
      setIsRestoringData(true); // Start restoration

      const savedFormData = localStorage.getItem("summerCampSearchForm");
      let dataToRestore: any = savedFormData
        ? JSON.parse(savedFormData)
        : { ...initialFormData };

      setFormData(dataToRestore);
      // Restore school data if venue was selected
      if (dataToRestore.venueSchool) {
        await fetchAndProcessSchoolData(
          dataToRestore.venueSchool,
          showPrivate,
          dataToRestore.campType, // Pass campType to populate months
        );
      }

      setIsRestoringData(false); // Restoration complete
    };

    restoreSavedFormData();
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      localStorage.setItem("summerCampSearchForm", JSON.stringify(formData));
    }
    if (!formData.studentType) {
      if (app_name === APP_NAME.SUMMER_CAMP_PRIVATE) {
        SetIsExternalApp(true);
        setFormData((prev) => ({
          ...prev,
          studentType: {
            label: "NON-VIBGYOR-STUDENT",
            value: CommonConstant.non_vibgyor_student,
          },
        }));
      }
    }
  }, [formData]);
  useEffect(() => {
    if (isRestoringData) {
      return;
    }

    const shouldUpdateVenues =
      cachedSchoolEvents.current.length > 0 &&
      formData.campType &&
      formData.campMonth;

    if (shouldUpdateVenues) {
      const venueOptions = getVenueTypeOptions(
        cachedSchoolEvents.current,
        formData.campType,
        formData.campMonth,
        formData.grade,
        formData.gender,
      );
      setVenueTypeOptions(venueOptions);

      const isCurrentVenueAvailable =
        formData.venueType &&
        venueOptions.some((opt) => opt.value === formData.venueType.value);

      if (!isCurrentVenueAvailable) {
        setFormData((prev) => ({
          ...prev,
          venueType: venueOptions.length === 1 ? venueOptions[0] : null,
        }));
      }
      if (venueOptions.length === 0) {
        toast.info(
          "No venues available for the selected options.",
          ToastOptions,
        );
      }
    } else {
      setVenueTypeOptions([]);
      if (formData.venueType) {
        setFormData((prev) => ({ ...prev, venueType: null }));
      }
    }
  }, [
    formData.campType,
    formData.campMonth,
    formData.grade,
    formData.gender,
    eventsVersion,
    isRestoringData,
  ]);
  // Function to clear form data and localStorage
  const clearAllData = () => {
    setShowError({});
    setFormData({ ...initialFormData });
    localStorage.removeItem("summerCampSearchForm");
    campMonthOptions.current = [];
  };

  return (
    <>
      {(loading || isLoadingSchoolData) && <Loader />}
      <style>{`
        .element-block {
          width: 48%;
        }

        @media (max-width: 768px) {
          .element-block {
            width: 100%;
          }
        }
      `}</style>
      <div
        className="m-4 d-flex justify-content-center custom-container"
        style={{
          background: "#F5F5FF",
          padding: "100px 0px 60px 0px",
        }}
      >
        <div className="d-flex flex-column gap-2 col-12 col-md-10">
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "10px",
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: "48px" }}>
              {isExternalApp && "Olympia Summer "}Camp
            </div>
            {/* <div className="d-flex flex-column flex-md-row">
              {!isExternalApp &&
                formData.venueSchool &&
                categorizedLinks?.transport && (
                  <DownloadLinkButton
                    href={categorizedLinks.transport.href}
                    label={categorizedLinks.transport.name}
                    icon={<DirectionsBusIcon />}
                    variant="primary"
                    className="px-5 py-2"
                    style={{
                      borderRadius: "50px",
                      width: "100%",
                      height: "42px",
                      marginTop: "9px",
                    }}
                  />
                )}
            </div> */}
          </div>

          <div
            className="d-flex flex-column gap-2"
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "15px",
            }}
          >
            <div className="d-flex flex-column gap-2 mb-2 mb-md-2">
              {(!formData.campType ||
                !formData.venueSchool ||
                !formData.campMonth) && (
                <div className="d-flex flex-column gap-2">
                  <div
                    className="alert alert-warning"
                    style={{ width: "100%" }}
                    role="alert"
                  >
                    Please select Camp Type, School, and Duration to proceed.
                  </div>
                </div>
              )}
              <div className="fs-5">
                Camp Type<span className="text-danger">*</span>
              </div>
              <div className="d-flex flex-wrap gap-4">
                {SummerCampTypes.map((element) => {
                  const label =
                    SummerCampTypesTexts.find((type) => type.value === element)
                      ?.label || element;
                  return (
                    <div className="d-flex" key={element}>
                      <button
                        type="button"
                        className={`rounded-pill btn`}
                        onClick={() => handleCampType(element)}
                        style={{
                          minWidth: "10rem",
                          backgroundColor:
                            formData.campType === element ? "#3F41D1" : "",
                          color: formData.campType === element ? "#ffffff" : "",
                          border: "1px solid #3F41D1",
                        }}
                        disabled={formLoading}
                      >
                        <div>{label}</div>
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="form-error-msg">
                {/* {showError.campMonth && <span>Camp Type is required.</span>} */}
              </div>
            </div>
            {PRIVATE_PRODUCTS_ACCESS_ROLES.includes(userRole) && (
              <div className="col-12">
                <div
                  style={{
                    color: "#212121",
                    fontWeight: "500",
                    fontSize: "1.25rem",
                  }}
                >
                  Private Events
                </div>
                <div className="form-check form-switch d-flex align-items-end gap-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="showPrivateEvents"
                    style={{
                      cursor: "pointer",
                      height: "1.5rem",
                      width: "3rem",
                    }}
                    checked={showPrivate}
                    onChange={handlePrivateProducts}
                  />

                  <label
                    className="form-check-label"
                    htmlFor="showPrivateEvents"
                    style={{ cursor: "pointer", height: "1.5rem" }}
                  >
                    Show Private Events
                  </label>
                </div>
              </div>
            )}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 gap-md-4">
              {!isExternalApp && (
                <>
                  <div className="element-block">
                    <ReUsableSelect
                      label={"Student Type"}
                      instanceId={"summer-camp-student-type"}
                      onChange={handleStudentType}
                      options={StudentTypes}
                      value={formData?.studentType}
                      placeholder={"Search student type..."}
                      formLoading={formLoading}
                      styles={{
                        control: (provided: any, state: any) => ({
                          ...provided,
                          borderColor: showError.studentType
                            ? "#dc2626"
                            : "#3F41D1",
                          boxShadow: state.isFocused
                            ? `0 0 0 1px ${
                                showError.studentType ? "#dc2626" : "#3F41D1"
                              }`
                            : provided.boxShadow,
                          "&:hover": {
                            borderColor: showError.studentType
                              ? "#dc2626"
                              : "#3F41D1",
                          },
                        }),
                        option: (provided: any, state: any) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? "#3F41D1"
                            : state.isFocused
                              ? "#f3f4f6"
                              : "white",
                          color: state.isSelected ? "white" : "black",
                        }),
                      }}
                    />
                    <div className="form-error-msg">
                      {showError.studentType && (
                        <span>Student Type is required.</span>
                      )}
                    </div>
                  </div>

                  <div className="element-block">
                    <div className="d-flex flex-column gap-2">
                      <div className="fs-5">
                        ENR No./GRN No.
                        {formData.studentType?.value !==
                          CommonConstant.non_vibgyor_student && (
                          <span className="text-danger">*</span>
                        )}
                      </div>
                      <div
                        className="d-flex gap-1 align-items-center"
                        style={{
                          // backgroundColor: "#E9ECEF",
                          borderRadius: "0.375rem",
                          border:
                            formData.studentType?.value !==
                              CommonConstant.non_vibgyor_student &&
                            showError.enrollmentNumber
                              ? "1px solid #dc2626"
                              : "1px solid #3F41D1",
                        }}
                      >
                        <input
                          type="text"
                          name="enrollmentNumber"
                          className="form-control d-flex align-items-center"
                          placeholder="ENXXXXXXXXX"
                          value={formData.enrollmentNumber}
                          onChange={handleEnrollmentNumberChange}
                          onBlur={(e) => onENRNoBlur(e)}
                          disabled={formLoading}
                        />
                      </div>
                    </div>
                    <div className="form-error-msg">
                      {formData.studentType?.value !==
                        CommonConstant.non_vibgyor_student &&
                        showError.enrollmentNumber && (
                          <span>ENR No./GRN No. is required.</span>
                        )}
                    </div>
                  </div>
                </>
              )}
              <div className="element-block">
                <ReUsableSelect
                  label={"Select Grade"}
                  instanceId={"summer-camp-grade"}
                  onChange={handleGradeChange}
                  options={formOptions?.grades || []}
                  value={formData?.grade}
                  placeholder={"Search Grade..."}
                  formLoading={formLoading}
                  styles={{
                    control: (provided: any, state: any) => ({
                      ...provided,
                      borderColor: showError.grade ? "#dc2626" : "#3F41D1",
                      boxShadow: state.isFocused
                        ? `0 0 0 1px ${showError.grade ? "#dc2626" : "#3F41D1"}`
                        : provided.boxShadow,
                      "&:hover": {
                        borderColor: showError.grade ? "#dc2626" : "#3F41D1",
                      },
                    }),
                    option: (provided: any, state: any) => ({
                      ...provided,
                      backgroundColor: state.isSelected
                        ? "#3F41D1"
                        : state.isFocused
                          ? "#f3f4f6"
                          : "white",
                      color: state.isSelected ? "white" : "black",
                    }),
                    menuList: (provided: any) => ({
                      ...provided,
                      maxHeight: 180, // approx height for 5 items (5 * 40px)
                      overflowY: "auto",
                    }),
                  }}
                />
                <div className="form-error-msg">
                  {showError.grade && <span>Grade is required.</span>}
                </div>
              </div>
              <div className="element-block">
                <ReUsableSelect
                  label={"Venue School"}
                  instanceId={ORDER_TYPE.SUMMER_CAMP}
                  onChange={async (e: any) => handleSchoolChange(e)}
                  options={formOptions?.schools || []}
                  value={formData?.venueSchool}
                  placeholder={"Search School..."}
                  formLoading={formLoading}
                  styles={{
                    control: (provided: any, state: any) => ({
                      ...provided,
                      borderColor: showError.venueSchool
                        ? "#dc2626"
                        : "#3F41D1",
                      boxShadow: state.isFocused
                        ? `0 0 0 1px ${
                            showError.venueSchool ? "#dc2626" : "#3F41D1"
                          }`
                        : provided.boxShadow,
                      "&:hover": {
                        borderColor: showError.venueSchool
                          ? "#dc2626"
                          : "#3F41D1",
                      },
                    }),
                    option: (provided: any, state: any) => ({
                      ...provided,
                      backgroundColor: state.isSelected
                        ? "#3F41D1"
                        : state.isFocused
                          ? "#f3f4f6"
                          : "white",
                      color: state.isSelected ? "white" : "black",
                    }),
                  }}
                />
                <div className="form-error-msg">
                  {showError.venueSchool && (
                    <span>Venue School is required.</span>
                  )}
                </div>
              </div>
              <div className="element-block">
                <ReUsableSelect
                  label={"Select Duration"}
                  instanceId={"summer-camp-month"}
                  onChange={handleCampMonthChange}
                  options={campMonthOptions.current}
                  value={formData?.campMonth}
                  placeholder={
                    isRestoringData || isLoadingSchoolData
                      ? "Loading durations..."
                      : !formData.campType?.trim()
                        ? "Please select a Camp Type"
                        : formData.venueSchool === null
                          ? "Please select a Venue School"
                          : !campMonthOptions.current ||
                              campMonthOptions.current.length === 0
                            ? "No camp durations available"
                            : "Search camp duration"
                  }
                  formLoading={
                    isRestoringData ||
                    isLoadingSchoolData ||
                    formLoading ||
                    !formData.campType?.trim() ||
                    !campMonthOptions.current ||
                    campMonthOptions.current.length === 0
                  }
                  styles={{
                    control: (provided: any, state: any) => ({
                      ...provided,
                      borderColor: showError.campMonth ? "#dc2626" : "#3F41D1",
                      boxShadow: state.isFocused
                        ? `0 0 0 1px ${
                            showError.campMonth ? "#dc2626" : "#3F41D1"
                          }`
                        : provided.boxShadow,
                      "&:hover": {
                        borderColor: showError.campMonth
                          ? "#dc2626"
                          : "#3F41D1",
                      },
                    }),
                    option: (provided: any, state: any) => ({
                      ...provided,
                      backgroundColor: state.isSelected
                        ? "#3F41D1"
                        : state.isFocused
                          ? "#f3f4f6"
                          : "white",
                      color: state.isSelected ? "white" : "black",
                    }),
                  }}
                />
                <div className="form-error-msg">
                  {showError.campMonth && <span>Duration is required.</span>}
                </div>
              </div>

              <div className="element-block">
                <ReUsableSelect
                  label={"Select Gender"}
                  instanceId={"summer-camp-gender"}
                  onChange={handleGenderChange}
                  options={GenderOptions}
                  value={formData?.gender}
                  placeholder={"Search Gender..."}
                  formLoading={formLoading}
                  styles={{
                    control: (provided: any, state: any) => ({
                      ...provided,
                      borderColor: showError.gender ? "#dc2626" : "#3F41D1",
                      boxShadow: state.isFocused
                        ? `0 0 0 1px ${
                            showError.gender ? "#dc2626" : "#3F41D1"
                          }`
                        : provided.boxShadow,
                      "&:hover": {
                        borderColor: showError.gender ? "#dc2626" : "#3F41D1",
                      },
                    }),
                    option: (provided: any, state: any) => ({
                      ...provided,
                      backgroundColor: state.isSelected
                        ? "#3F41D1"
                        : state.isFocused
                          ? "#f3f4f6"
                          : "white",
                      color: state.isSelected ? "white" : "black",
                    }),
                  }}
                />
                <div className="form-error-msg">
                  {showError.gender && <span>Gender is required.</span>}
                </div>
              </div>
              <div className="element-block">
                <ReUsableSelect
                  label={"Select Venue Type"}
                  instanceId={"summer-camp-venue-type"}
                  onChange={handleVenueTypeChange}
                  options={venueTypeOptions}
                  value={formData?.venueType}
                  placeholder={
                    !formData.campType?.trim()
                      ? "Please select a Camp Type"
                      : formData.venueSchool === null
                        ? "Please select a Venue School"
                        : !formData.campMonth
                          ? "Please select a Duration"
                          : isLoadingSchoolData
                            ? "Loading venue types..."
                            : venueTypeOptions.length === 0
                              ? "No venue types available"
                              : "Select venue type"
                  }
                  formLoading={
                    isRestoringData ||
                    isLoadingSchoolData ||
                    formLoading ||
                    !formData.campType?.trim() ||
                    !formData.venueSchool ||
                    !formData.campMonth ||
                    venueTypeOptions.length === 0
                  }
                  styles={{
                    control: (provided: any, state: any) => ({
                      ...provided,
                      borderColor: showError.venueType ? "#dc2626" : "#3F41D1",
                      boxShadow: state.isFocused
                        ? `0 0 0 1px ${
                            showError.venueType ? "#dc2626" : "#3F41D1"
                          }`
                        : provided.boxShadow,
                      "&:hover": {
                        borderColor: showError.venueType
                          ? "#dc2626"
                          : "#3F41D1",
                      },
                    }),
                    option: (provided: any, state: any) => ({
                      ...provided,
                      backgroundColor: state.isSelected
                        ? "#3F41D1"
                        : state.isFocused
                          ? "#f3f4f6"
                          : "white",
                      color: state.isSelected ? "white" : "black",
                    }),
                  }}
                />

                <div className="form-error-msg">
                  {showError.venueType && <span>Venue type is required.</span>}
                </div>
              </div>
            </div>

            <div
              style={{ marginBottom: "1rem", marginTop: "2rem" }}
              className="d-flex gap-2 flex-column flex-md-row justify-content-md-end"
            >
              <button
                type="button"
                className="px-5 py-2 d-flex gap-2 align-items-center justify-content-center"
                style={{
                  background: "#808080",
                  color: "#fff",
                  borderRadius: "50px",
                }}
                onClick={() => clearAllData()}
                disabled={formLoading}
              >
                <div className="d-flex align-items-end">Clear All</div>
                <Image src={TrashIcon} alt="" />
              </button>
              <button
                type="button"
                className="primary-button px-5 py-2 d-flex gap-2 align-items-center justify-content-center"
                style={{
                  borderRadius: "50px",
                }}
                // disabled={!validateForm() || formLoading || isMaxcapacity}
                disabled={formLoading || isMaxcapacity}
                onClick={searchEvent}
              >
                <div>Search for Events</div>
                {formLoading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  <Image src={EventCalenderIcon} alt="" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Page;
